// Emploi Dashboard - Navigation interne au Cockpit
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Briefcase, Bookmark, FileText, User, Bell, Search, MapPin,
  Building2, Clock, Euro, ChevronRight, TrendingUp, PlusCircle, Menu
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import CockpitParticulierBottomNav from '../../../components/layout/CockpitParticulierBottomNav';

function StatCard({ title, value, icon: Icon, color, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl p-5 border border-gray-100 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="text-xl font-semibold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
        </div>
        {onClick && (
          <ChevronRight size={20} className="text-cyan-600" />
        )}
      </div>
    </motion.div>
  );
}

function OffreCard({ offre }) {
  return (
    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
      <div className="w-12 h-12 bg-white rounded-xl border border-gray-200 flex items-center justify-center flex-shrink-0">
        <Building2 size={24} className="text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{offre.title}</h4>
        <p className="text-xs text-cyan-600">{offre.company}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <MapPin size={10} />
            {offre.location}
          </span>
          <span className="flex items-center gap-1">
            <Euro size={10} />
            {offre.salary}
          </span>
        </div>
      </div>
      <span className="text-xs text-gray-400">{offre.date}</span>
    </div>
  );
}

export default function EmploiDashboard({ onNavigate, onOpenMenu }) {
  const { user } = useCurrentUser();
  
  // Stats démo vs vierge
  const DEMO_STATS = {
    offresSauvegardees: 8,
    candidatures: 5,
    entretiens: 2,
    alertesActives: 3,
  };
  
  const EMPTY_STATS = {
    offresSauvegardees: 0,
    candidatures: 0,
    entretiens: 0,
    alertesActives: 0,
  };

  const DEMO_OFFRES = [];

  const [stats, setStats] = useState(EMPTY_STATS);
  const [offresRecentes, setOffresRecentes] = useState([]);

  useEffect(() => {
    if (user?.isDemo) {
      setStats(DEMO_STATS);
      setOffresRecentes(DEMO_OFFRES);
    } else {
      setStats(EMPTY_STATS);
      setOffresRecentes([]);
    }
  }, [user]);

  // Navigation interne au cockpit
  const navigateTo = (context, label) => {
    if (onNavigate) {
      onNavigate(context, label);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style identique à Mon Espace */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            {/* Menu Hamburger */}
            {onOpenMenu && (
              <button
                onClick={onOpenMenu}
                className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="hamburger-menu"
              >
                <Menu size={22} className="text-[#1E3A5F]" />
              </button>
            )}
            <div>
              <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Emploi</h1>
              <p className="text-sm text-gray-500">Trouve ton prochain emploi</p>
            </div>
          </div>
          <button 
            onClick={() => navigateTo('page_emploi', 'Emploi')}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors text-sm h-9 shadow-sm"
          >
            <Search size={16} />
            <span className="hidden sm:inline">Rechercher</span>
          </button>
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Briefcase size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">
              {stats.candidatures} candidature{stats.candidatures > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard
          title="Offres sauvegardées"
          value={stats.offresSauvegardees}
          icon={Bookmark}
          color="bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A]"
          onClick={() => navigateTo('offres_sauvegardees', 'Offres sauvegardées')}
        />
        <StatCard
          title="Candidatures"
          value={stats.candidatures}
          icon={FileText}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
          onClick={() => navigateTo('candidatures', 'Mes Candidatures')}
        />
        <StatCard
          title="Entretiens"
          value={stats.entretiens}
          icon={Briefcase}
          color="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Alertes actives"
          value={stats.alertesActives}
          icon={Bell}
          color="bg-gradient-to-br from-amber-500 to-amber-600"
          onClick={() => navigateTo('alertes_emploi', 'Alertes emploi')}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 flex-1 overflow-hidden">
        {/* Offres récentes */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1E3A5F]">Offres correspondant à ton profil</h2>
            <button 
              onClick={() => navigateTo('page_emploi', 'Emploi')}
              className="text-xs text-orange-500 font-medium hover:text-orange-600 flex items-center gap-1"
            >
              Voir tout <ChevronRight size={12} />
            </button>
          </div>
          <div className="p-3 space-y-2 flex-1 overflow-y-auto">
            {offresRecentes.length > 0 ? (
              offresRecentes.map(offre => (
                <OffreCard key={offre.id} offre={offre} />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center mb-3">
                  <Briefcase size={20} className="text-orange-500" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Aucune offre sauvegardée</h3>
                <p className="text-xs text-gray-500 mb-3">Explore les offres d'emploi disponibles</p>
                <button 
                  onClick={() => navigateTo('page_emploi', 'Emploi')}
                  className="text-xs text-orange-500 font-semibold hover:text-orange-600"
                >
                  Explorer les offres →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mon CV */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1E3A5F]">Mon CV</h2>
            <button 
              onClick={() => navigateTo('mon_cv', 'Mon CV')}
              className="text-xs text-orange-500 font-medium hover:text-orange-600 flex items-center gap-1"
            >
              {user?.isDemo ? 'Modifier' : 'Créer'} <ChevronRight size={12} />
            </button>
          </div>
          {user?.isDemo ? (
            <div className="p-4 flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0">
                  JD
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Jean Dupont</h3>
                  <p className="text-xs text-orange-500 font-medium">Plombier qualifié</p>
                </div>
              </div>
              
              <div className="space-y-2.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 text-xs">Expérience</span>
                  <span className="font-medium text-gray-900 text-xs">8 ans</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 text-xs">Localisation</span>
                  <span className="font-medium text-gray-900 text-xs">Lyon et alentours</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 text-xs">Disponibilité</span>
                  <span className="font-medium text-green-600 text-xs">Immédiate</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 text-xs">Complétude CV</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="w-3/4 h-full bg-orange-500 rounded-full" />
                    </div>
                    <span className="font-medium text-gray-900 text-xs">75%</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 flex-1 flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center mb-3">
                <User size={20} className="text-orange-500" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">CV non créé</h3>
              <p className="text-xs text-gray-500 mb-3">Crée ton CV pour postuler aux offres</p>
              <Button 
                size="sm" 
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                onClick={() => navigateTo('mon_cv', 'Mon CV')}
              >
                Créer mon CV
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* CTA - Style harmonisé */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingUp size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#1E3A5F] mb-0.5">Optimise tes chances</h3>
            <p className="text-xs text-gray-600 mb-2">
              Complete ton CV à 100% pour apparaître en priorité auprès des recruteurs.
            </p>
            <button 
              onClick={() => navigateTo('mon_cv', 'Mon CV')}
              className="text-xs text-orange-500 font-semibold hover:text-orange-600"
            >
              Compléter mon CV →
            </button>
          </div>
        </div>
      </div>
      
      {/* Navbar fixe en bas - Mobile uniquement */}
      <CockpitParticulierBottomNav />
    </div>
  );
}
